import { HeaderIcons } from '../../shared/icons';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Header from '../../shared/Header';
import '../../shared/TimerBar.css';
import WaitingSpinner from '../../shared/WaitingSpinner';
import {
  BonusLevelPayload,
  BONUS_INTRO_MS,
} from '../../../constants/bonusConstants';
import { getBonusChainCompleteMessages, getBonusIntro } from '../../../i18n/helpers';
import { useHostPlaying } from '../../../hooks/useHostPlaying';
import { WhosOnImagePlayer } from './whosOnImage/WhosOnImagePlayer';
import { WordOrderPlayer } from './wordOrder/WordOrderPlayer';
import { FastTypingPlayer } from './fastTyping/FastTypingPlayer';
import { FastFingersPlayer } from './fastFingers/FastFingersPlayer';
import { CatchTheTrainPlayer } from './catchTheTrain/CatchTheTrainPlayer';
import { SaveTheAnimalsPlayer } from './saveTheAnimals/SaveTheAnimalsPlayer';
import { PuzzlePlayer } from './puzzle/PuzzlePlayer';
import { QuickMathPlayer } from './quickMath/QuickMathPlayer';
import { getRandomPuzzleImageUrl } from '../../../utils/bonusPuzzleImage';
import { useTranslation } from '../../../i18n';

interface BonusLevelPlayerProps {
  roomState: {
    roomName: string;
    hostId: string;
    players: Array<{ id: string }>;
    isBonusLevel?: boolean;
    bonusLevelPayload?: BonusLevelPayload | null;
    bonusStartTime?: number;
  };
  socket: {
    emit: (event: string, data: any) => void;
    on: (event: string, cb: (data: any) => void) => void;
    off: (event: string, cb?: (data: any) => void) => void;
  };
  myPlayerId: string;
  onGoHome?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

interface BonusResult {
  questionId?: string;
  bonusGameType?: string;
  correctAnswer?: string;
  submissions?: Array<{ playerId: string; playerName: string; answer: string; points: number; isCorrect?: boolean }>;
  leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
}

/**
 * Player view during bonus.
 * When host is not playing: only the active game UI (no intro/result).
 * When host is playing: full experience (intro + timer + game + result).
 *
 * Listens for bonus-level-result to freeze input, show overlay with correct
 * answer and points.  This provides the unified outro for all bonus games.
 */
export const BonusLevelPlayer: React.FC<BonusLevelPlayerProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  isHost,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const hostIsPlaying = useHostPlaying(roomState);
  const payload = roomState.bonusLevelPayload ?? null;
  const bonusStartTime = roomState.bonusStartTime ?? Date.now();
  const timeLimit = payload?.timeLimit ?? 30000;
  /** For timer-based games (fast-typing, fast-fingers): submit when round ends, i.e. intro + game time */
  const roundDurationMs = timeLimit + BONUS_INTRO_MS;

  // ── Local timer (syncs with host's timer for near-simultaneous "Time's Up") ──
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  const [timeUp, setTimeUp] = useState(false);

  // ── Bonus result / round-ended state ──
  const [bonusResult, setBonusResult] = useState<BonusResult | null>(null);
  const [playerSubmitted, setPlayerSubmitted] = useState(false);
  const [myPointsFromAck, setMyPointsFromAck] = useState<number | undefined>(undefined);
  const playerSubmittedRef = useRef(false);
  /** Latest fast-fingers score (updated via onScoreChange) */
  const fastFingersScoreRef = useRef(0);
  /** Latest catch-the-train score (updated via onScoreChange) */
  const catchTheTrainScoreRef = useRef(0);
  /** Latest save-the-animals score (updated via onScoreChange) */
  const saveTheAnimalsScoreRef = useRef(0);
  /** Latest fast-typing answer (updated via onAnswerChange) – for late-submit when bonus-level-result arrives without prior submit */
  const fastTypingAnswerRef = useRef('');
  /** Track the payload questionId so we can reset state on new bonus */
  const bonusQuestionIdRef = useRef<string | undefined>(payload?.questionId);
  /** Quick-math: finished all steps before timer; show same overlay UX as time-up with praise copy */
  const [quickMathChainComplete, setQuickMathChainComplete] = useState(false);
  /** When host is also a player, BonusHostScreen never mounts — host must broadcast puzzle image from here (once per question). */
  const hostPlayingPuzzleImageSentForRef = useRef<string | undefined>(undefined);
  /** Keep puzzle image URL for thumbnail when room-state briefly drops imageUrl (same idea as BonusLevelHost). */
  const puzzleThumbPersistRef = useRef<{ questionId: string; url: string } | null>(null);

  // Round ended = local timer hit 0 OR server result arrived
  const roundEnded = timeUp || !!bonusResult;

  /** Sent final score on result (fast-fingers) so we only send once */
  const lateFastFingersSentRef = useRef(false);
  /** Sent final score on result (catch-the-train) so we only send once */
  const lateCatchTheTrainSentRef = useRef(false);
  /** Sent final score on result (save-the-animals) so we only send once */
  const lateSaveTheAnimalsSentRef = useRef(false);
  /** Sent late answer on result (fast-typing) so we only send once */
  const lateFastTypingSentRef = useRef(false);

  // Reset state when a new bonus round starts
  useEffect(() => {
    if (payload?.questionId && payload.questionId !== bonusQuestionIdRef.current) {
      bonusQuestionIdRef.current = payload.questionId;
      hostPlayingPuzzleImageSentForRef.current = undefined;
      setTimeUp(false);
      setTimeRemaining(payload.timeLimit ?? 30000);
      setBonusResult(null);
      setPlayerSubmitted(false);
      setMyPointsFromAck(undefined);
      playerSubmittedRef.current = false;
      fastFingersScoreRef.current = 0;
      catchTheTrainScoreRef.current = 0;
      saveTheAnimalsScoreRef.current = 0;
      fastTypingAnswerRef.current = '';
      lateFastFingersSentRef.current = false;
      lateCatchTheTrainSentRef.current = false;
      lateSaveTheAnimalsSentRef.current = false;
      lateFastTypingSentRef.current = false;
      setQuickMathChainComplete(false);
    }
  }, [payload?.questionId, payload?.timeLimit]);

  // Host + playing: pick puzzle image and broadcast (normally done from BonusHostScreen intro).
  useEffect(() => {
    if (!isHost || !hostIsPlaying) return;
    if (!payload?.questionId || payload.bonusGameType !== 'puzzle') return;
    if (!roomState.roomName) return;
    if (hostPlayingPuzzleImageSentForRef.current === payload.questionId) return;
    hostPlayingPuzzleImageSentForRef.current = payload.questionId;
    const imageUrl = getRandomPuzzleImageUrl();
    socket.emit('bonus-puzzle-set-image', { roomName: roomState.roomName, imageUrl });
  }, [isHost, hostIsPlaying, payload?.bonusGameType, payload?.questionId, roomState.roomName, socket]);

  // Tick timer: backend ends at bonusStartTime + BONUS_INTRO_MS + timeLimit
  // Player sees game immediately (no intro), so remaining = until backend ends
  useEffect(() => {
    if (!payload || timeUp) return;
    const interval = setInterval(() => {
      const roundEndTime = bonusStartTime + BONUS_INTRO_MS + timeLimit;
      const remaining = Math.max(0, roundEndTime - Date.now());
      setTimeRemaining(remaining);
      if (remaining <= 0) setTimeUp(true);
    }, 200);
    return () => clearInterval(interval);
  }, [payload, bonusStartTime, timeLimit, timeUp]);

  // Listen for bonus-time-up: server says game over → submit immediately (triggers disabled → child backup submit)
  useEffect(() => {
    const onTimeUp = () => {
      setTimeUp(true);
    };
    socket.on('bonus-time-up', onTimeUp);
    return () => { socket.off('bonus-time-up', onTimeUp); };
  }, [socket]);

  // Listen for bonus-level-result (arrives after grace period)
  useEffect(() => {
    const onResult = (data: BonusResult) => {
      setTimeUp(true); // ensure overlay is shown
      setBonusResult(data);

      // Fast-fingers: if BE didn't have our score (0 in result), send final score once so BE/host can update
      if (data?.bonusGameType === 'fast-fingers' && !lateFastFingersSentRef.current) {
        const mySub = data?.submissions?.find((s) => s.playerId === myPlayerId);
        const myPointsInResult = mySub?.points ?? 0;
        const localScore = fastFingersScoreRef.current;
        if (localScore > 0 && myPointsInResult === 0) {
          lateFastFingersSentRef.current = true;
          socket.emit('bonus-answer', {
            roomName: roomState.roomName,
            answer: 'time-up',
            points: localScore,
          });
        }
      }

      // Catch-the-train: if BE didn't have our score (0 in result), send final score once so BE/host can update
      if (data?.bonusGameType === 'catch-the-train' && !lateCatchTheTrainSentRef.current) {
        const mySub = data?.submissions?.find((s) => s.playerId === myPlayerId);
        const myPointsInResult = mySub?.points ?? 0;
        const localPoints = catchTheTrainScoreRef.current;
        if (localPoints > 0 && myPointsInResult === 0) {
          lateCatchTheTrainSentRef.current = true;
          socket.emit('bonus-answer', {
            roomName: roomState.roomName,
            answer: 'time-up',
            points: localPoints,
          });
        }
      }

      // Save-the-animals: if BE didn't have our score (0 in result), send final score once so BE/host can update
      if (data?.bonusGameType === 'catch-me-if-you-can' && !lateSaveTheAnimalsSentRef.current) {
        const mySub = data?.submissions?.find((s) => s.playerId === myPlayerId);
        const myPointsInResult = mySub?.points ?? 0;
        const localPoints = saveTheAnimalsScoreRef.current;
        if (localPoints > 0 && myPointsInResult === 0) {
          lateSaveTheAnimalsSentRef.current = true;
          socket.emit('bonus-answer', {
            roomName: roomState.roomName,
            answer: 'time-up',
            points: localPoints,
          });
        }
      }

      // Fast-typing: if BE didn't have our submission (0 points in result) but we have typed text, send it as late-submit
      // (e.g. user didn't click Submit before round ended; bonus-time-up / disabled→doSubmit may have been missed)
      if (
        data?.bonusGameType === 'fast-typing' &&
        !lateFastTypingSentRef.current &&
        !playerSubmittedRef.current
      ) {
        const mySub = data?.submissions?.find((s) => s.playerId === myPlayerId);
        const myPointsInResult = mySub?.points ?? 0;
        const localAnswer = fastTypingAnswerRef.current?.trim() ?? '';
        if (localAnswer.length > 0 && myPointsInResult === 0) {
          lateFastTypingSentRef.current = true;
          socket.emit('bonus-answer', {
            roomName: roomState.roomName,
            answer: localAnswer,
            points: 0, // BE will compute points from answer
          });
        }
      }
    };
    socket.on('bonus-level-result', onResult);
    return () => { socket.off('bonus-level-result', onResult); };
  }, [socket, roomState.roomName, myPlayerId]);

  // Listen for bonus-answer-ack (fast-typing, fast-fingers, quick-math): server sends points immediately on submit
  useEffect(() => {
    const onAck = (data: { points?: number }) => {
      if (typeof data.points === 'number') setMyPointsFromAck(data.points);
    };
    socket.on('bonus-answer-ack', onAck);
    return () => { socket.off('bonus-answer-ack', onAck); };
  }, [socket]);

  // ── Submit handler ──
  const handleBonusComplete = useCallback((answer: string, points?: number) => {
    if (playerSubmittedRef.current) return; // Prevent duplicate submissions
    playerSubmittedRef.current = true;
    setPlayerSubmitted(true);
    socket.emit('bonus-answer', {
      roomName: roomState.roomName,
      answer,
      points: points ?? 0,
    });
  }, [socket, roomState.roomName]);

  /** Track fast-fingers score for overlay display */
  const handleFastFingersScoreChange = useCallback((score: number) => {
    fastFingersScoreRef.current = score;
  }, []);

  /** Track catch-the-train score for overlay and late submit */
  const handleCatchTheTrainScoreChange = useCallback((score: number) => {
    catchTheTrainScoreRef.current = score;
  }, []);

  /** Track save-the-animals score for overlay and late submit */
  const handleSaveTheAnimalsScoreChange = useCallback((score: number) => {
    saveTheAnimalsScoreRef.current = score;
  }, []);

  /** Track fast-typing answer for late-submit when bonus-level-result arrives without prior submit */
  const handleFastTypingAnswerChange = useCallback((answer: string) => {
    fastTypingAnswerRef.current = answer;
  }, []);

  // ── Find my submission from result ──
  const mySubmission = bonusResult?.submissions?.find((s) => s.playerId === myPlayerId);
  const myPoints = mySubmission?.points ?? myPointsFromAck;
  const bonusGameType = payload?.bonusGameType ?? bonusResult?.bonusGameType;

  const quickMathPraise = useMemo(() => {
    const list = getBonusChainCompleteMessages();
    if (!list.length) return { title: t('bonus.chainComplete.wellDone.title'), subtitle: t('bonus.player.chainCompleteFallback') };
    const pick = Math.floor(Math.random() * list.length);
    return list[pick];
  }, [payload?.questionId]);

  /** Until next question, keep praise UX (avoid swapping to "Time's Up" after they already cleared the chain). */
  const showQuickMathChainOverlay = bonusGameType === 'quick-math' && quickMathChainComplete;

  // Show overlay with points as soon as player submitted and we have points (before round end)
  // Fast-typing/fast-fingers: points from ack. Whos-on-image/word-order: points from ack (server sends on submit).
  const showEarlyResult =
    playerSubmitted &&
    (((bonusGameType === 'fast-typing' ||
      bonusGameType === 'fast-fingers' ||
      bonusGameType === 'catch-the-train' ||
      bonusGameType === 'catch-me-if-you-can') &&
      myPointsFromAck !== undefined) ||
      ((bonusGameType === 'whos-on-image' || bonusGameType === 'word-order') && myPoints !== undefined));

  // ── Game content ──
  const isDisabled = roundEnded || playerSubmitted;

  const roundEndTimeMs = bonusStartTime + BONUS_INTRO_MS + timeLimit;

  /** Intro still running: timer bar includes BONUS_INTRO_MS before play clock counts down from timeLimit. */
  const isPuzzleIntro =
    payload?.bonusGameType === 'puzzle' && !roundEnded && timeRemaining > timeLimit;
  const puzzleIntroCountdownSeconds =
    hostIsPlaying && isPuzzleIntro
      ? Math.max(0, Math.ceil((timeRemaining - timeLimit) / 1000))
      : null;

  /** Seconds left in bonus intro (all clients); used when host plays so there is no cast intro/countdown. */
  const bonusIntroSecondsLeft =
    !roundEnded && timeRemaining > timeLimit
      ? Math.max(0, Math.ceil((timeRemaining - timeLimit) / 1000))
      : null;

  const showHostPlayingIntroBanner =
    hostIsPlaying &&
    !roundEnded &&
    bonusIntroSecondsLeft != null &&
    bonusIntroSecondsLeft > 0 &&
    payload &&
    payload.bonusGameType !== 'puzzle' &&
    payload.bonusGameType !== 'quick-math';

  const hostPlayingIntroBannerEl =
    showHostPlayingIntroBanner ? (
      <div
        role="status"
        aria-live="polite"
        style={{
          textAlign: 'center',
          padding: '0.85rem 1rem 0.35rem',
          marginBottom: '0.15rem',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 'clamp(1.45rem, 6.5vw, 2.2rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            color: 'var(--color-primary)',
            textShadow: '0 0 20px rgba(var(--color-primary-rgb), 0.35)',
          }}
        >
          {t('bonus.player.startingIn', { seconds: bonusIntroSecondsLeft })}
        </span>
      </div>
    ) : null;

  if (payload?.bonusGameType === 'puzzle' && payload.questionId && payload.imageUrl) {
    puzzleThumbPersistRef.current = { questionId: payload.questionId, url: payload.imageUrl };
  }
  const puzzleReferenceImageUrl =
    payload?.bonusGameType === 'puzzle'
      ? (payload.imageUrl ||
          (puzzleThumbPersistRef.current?.questionId === payload?.questionId
            ? puzzleThumbPersistRef.current.url
            : ''))
      : '';

  const wordOrderTarget = payload?.correctAnswer ?? '';
  const wordOrderLen = Math.max(wordOrderTarget.length, 1);
  /** Tighter vw on 7+ letters so the host preview fits iPhone mini without horizontal scroll */
  const wordOrderVwHint = Math.min(12, (wordOrderLen >= 7 ? 70 : 78) / wordOrderLen);

  const puzzlePlayerEl = (
    <PuzzlePlayer
      imageUrl={payload?.imageUrl ?? ''}
      roundEndTimeMs={roundEndTimeMs}
      timeLimitMs={timeLimit}
      onSubmit={(answer, points) => handleBonusComplete(answer, points)}
      disabled={isDisabled}
      isIntro={isPuzzleIntro}
      footerHint={
        hostIsPlaying ? t('bonus.player.puzzleHint') : undefined
      }
      introCountdownSeconds={hostIsPlaying ? puzzleIntroCountdownSeconds : null}
    />
  );

  const wordOrderPlayerEl =
    payload?.bonusGameType === 'word-order' ? (
      <WordOrderPlayer
        correctAnswer={payload.correctAnswer ?? ''}
        onSubmit={(answer) => handleBonusComplete(answer)}
        disabled={isDisabled}
      />
    ) : null;

  const gameContent =
    payload?.bonusGameType === 'puzzle' ? (
      hostIsPlaying ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '0.75rem',
          }}
        >
          {puzzleReferenceImageUrl ? (
            <img
              src={puzzleReferenceImageUrl}
              alt=""
              style={{
                maxHeight: 'min(32vw, 160px)',
                maxWidth: 'min(88vw, 340px)',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 10,
                border: '2px solid var(--color-primary)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
              }}
            />
          ) : null}
          {puzzlePlayerEl}
        </div>
      ) : (
        puzzlePlayerEl
      )
    ) : payload?.bonusGameType === 'whos-on-image' ? (
      <WhosOnImagePlayer
        imageUrl={payload.imageUrl ?? ''}
        fallbackImageUrl={payload.fallbackImageUrl}
        timeLimit={payload.timeLimit ?? 25000}
        onSubmit={(answer) => handleBonusComplete(answer)}
        disabled={isDisabled}
      />
    ) : payload?.bonusGameType === 'word-order' ? (
      hostIsPlaying ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '1rem',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              width: '100%',
              padding: '0 max(0.5rem, env(safe-area-inset-right)) 0 max(0.5rem, env(safe-area-inset-left))',
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                fontSize: `clamp(1.1rem, ${wordOrderVwHint}vw, 2.5rem)`,
                fontWeight: 'bold',
                letterSpacing:
                  wordOrderLen >= 9 ? '0.04em' : wordOrderLen >= 7 ? '0.05em' : wordOrderLen > 12 ? '0.06em' : '0.1em',
                color: 'var(--color-primary)',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {wordOrderTarget}
            </span>
          </div>
          {wordOrderPlayerEl}
        </div>
      ) : (
        wordOrderPlayerEl
      )
    )     : payload?.bonusGameType === 'fast-typing' ? (
      <FastTypingPlayer
        timeLimit={roundDurationMs}
        onSubmit={(answer) => handleBonusComplete(answer)}
        onAnswerChange={handleFastTypingAnswerChange}
        disabled={isDisabled}
        promptText={hostIsPlaying ? (payload.text ?? '') : undefined}
      />
    ) : payload?.bonusGameType === 'fast-fingers' ? (
      <FastFingersPlayer
        successEmoji={payload.successEmoji ?? '⚡'}
        wrongEmojis={payload.wrongEmojis ?? ['❌']}
        totalCount={payload.totalCount ?? 140}
        successCount={payload.successCount ?? 70}
        successPoints={payload.successPoints ?? 10}
        wrongPenalty={payload.wrongPenalty ?? 5}
        timeLimit={roundDurationMs}
        onSubmit={(answer, points) => handleBonusComplete(answer, points)}
        onScoreChange={handleFastFingersScoreChange}
        disabled={roundEnded}
      />
    ) : payload?.bonusGameType === 'catch-the-train' ? (
      <CatchTheTrainPlayer
        themeTitle={payload.themeTitle ?? getBonusIntro('catch-the-train').title}
        emoji={payload.emoji ?? '🚂'}
        timeLimit={roundDurationMs}
        onSubmit={(answer, points) => handleBonusComplete(answer, points)}
        onScoreChange={handleCatchTheTrainScoreChange}
        disabled={roundEnded}
      />
    ) : payload?.bonusGameType === 'catch-me-if-you-can' ? (
      <SaveTheAnimalsPlayer
        themeTitle={payload.themeTitle ?? getBonusIntro('catch-me-if-you-can').title}
        animals={payload.animals ?? ['🐱', '🐕', '🐦']}
        timeLimit={roundDurationMs}
        onSubmit={(answer, points) => handleBonusComplete(answer, points)}
        onScoreChange={handleSaveTheAnimalsScoreChange}
        disabled={roundEnded}
      />
    ) : payload?.bonusGameType === 'quick-math' ? (
      <QuickMathPlayer
        roomName={roomState.roomName}
        questionId={payload.questionId}
        steps={payload.steps ?? []}
        disabled={roundEnded}
        bonusIntroEndsAt={bonusStartTime + BONUS_INTRO_MS}
        introSecondsLeft={hostIsPlaying ? bonusIntroSecondsLeft : null}
        onChainComplete={() => setQuickMathChainComplete(true)}
        socket={socket}
      />
    ) : (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ marginBottom: '1rem' }}>{t('bonus.unknownGame', { type: payload?.bonusGameType ?? '—' })}</p>
        <button
          type="button"
          onClick={() => handleBonusComplete('Done', 100)}
          disabled={isDisabled}
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '1.1rem',
            borderRadius: 8,
            background: 'var(--color-primary)',
            color: '#fff',
            border: 'none',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          {t('bonus.player.imDone')}
        </button>
      </div>
    );

  // ── Points display value (works for all game types) ──
  const displayPoints =
    myPoints ??
    (bonusGameType === 'fast-fingers'
      ? fastFingersScoreRef.current
      : bonusGameType === 'catch-the-train'
        ? catchTheTrainScoreRef.current
        : bonusGameType === 'catch-me-if-you-can'
          ? saveTheAnimalsScoreRef.current
          : bonusGameType === 'quick-math'
            ? myPointsFromAck
            : undefined);

  // ── Timer progress bar (no seconds, just the shrinking bar). Cap at 100% when player has extra time (intro delay). ──
  const timerProgress = timeLimit > 0 ? Math.min(100, (timeRemaining / timeLimit) * 100) : 0;
  const timerSeconds = Math.ceil(timeRemaining / 1000);
  const isTimerWarning = timerSeconds <= 5;
  const timerBar = !roundEnded && (
    <div style={{ width: '100%', padding: '4.5rem 1rem 0', boxSizing: 'border-box' }}>
      <div style={{
        width: '100%',
        height: '8px',
        background: 'rgba(255, 255, 255, 0.15)',
        borderRadius: '4px',
        overflow: 'hidden',
        marginBottom: '0.75rem',
      }}>
        <div
          className={isTimerWarning ? 'timer-bar__slider-fill--warning' : ''}
          style={{
            width: `${timerProgress}%`,
            height: '100%',
            background: isTimerWarning ? '#EF4444' : 'var(--color-primary)',
            transition: 'width 0.2s linear',
            borderRadius: '4px',
          }}
        />
      </div>
    </div>
  );

  // ── End-of-round content: shown when local timer hits 0 (synced with host) ──
  // Submit-based games (whos-on-image, word-order) show correct answer + points.
  // Timer-based games (fast-fingers, fast-typing) show "Time's Up" + points.
  const correctAnswer = bonusResult?.correctAnswer ?? payload?.correctAnswer;
  const isSubmitGame = bonusGameType === 'whos-on-image' || bonusGameType === 'word-order' || bonusGameType === 'puzzle';
  /** For puzzle: only true when this player actually solved it (points > 0). */
  const puzzleSolved = bonusGameType === 'puzzle' && (myPoints ?? 0) > 0;

  const correctAnswerLen = Math.max((correctAnswer ?? '').length, 1);
  /** Long answers (e.g. word-order) shrink on narrow screens so the overlay does not overflow horizontally */
  const correctAnswerOverlayVw = Math.min(
    11,
    (correctAnswerLen >= 7 ? 72 : 82) / correctAnswerLen
  );

  const pointsBlock = (
    <>
      {displayPoints !== undefined && displayPoints > 0 ? (
        <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#34D399', margin: 0 }}>
          +{displayPoints} {t('common.pts')}
        </p>
      ) : displayPoints !== undefined && displayPoints <= 0 ? (
        <p style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
          {displayPoints} {t('common.pts')}
        </p>
      ) : playerSubmitted ? (
        <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
          {t('bonus.player.answerSubmitted')}
        </p>
      ) : null}
    </>
  );

  const overlay = (roundEnded || showEarlyResult || showQuickMathChainOverlay) && (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(15, 23, 42, 0.92)',
        borderRadius: '12px',
        padding: '3rem max(1rem, env(safe-area-inset-right)) 3rem max(1rem, env(safe-area-inset-left))',
        textAlign: 'center',
        animation: 'fadeIn 0.3s ease-out',
        minHeight: '40vh',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {isSubmitGame ? (
        <>
          {bonusGameType === 'puzzle' ? (
            <h2 style={{ fontSize: 'clamp(1.4rem, 4vw, 2rem)', fontWeight: 'bold', color: puzzleSolved ? 'var(--color-primary)' : 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>
              {puzzleSolved ? t('bonus.player.puzzleSolved') : t('bonus.player.puzzleNotSolved')}
            </h2>
          ) : correctAnswer ? (
            <>
              <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)', marginBottom: '0.4rem' }}>{t('bonus.player.correctAnswer')}</div>
              <h2
                style={{
                  fontSize: `clamp(1rem, ${correctAnswerOverlayVw}vw, 2.4rem)`,
                  fontWeight: 'bold',
                  color: 'var(--color-primary)',
                  marginBottom: '1rem',
                  letterSpacing:
                    correctAnswerLen >= 9
                      ? '0.03em'
                      : correctAnswerLen >= 7
                        ? '0.035em'
                        : correctAnswerLen > 14
                          ? '0.02em'
                          : '0.04em',
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {correctAnswer}
              </h2>
            </>
          ) : null}
          {pointsBlock}
        </>
      ) : showQuickMathChainOverlay ? (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🌟</div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#F8FAFC', marginBottom: '0.35rem' }}>
            {quickMathPraise.title}
          </h2>
          <p
            style={{
              fontSize: '1rem',
              color: 'rgba(248, 250, 252, 0.82)',
              margin: '0 0 1rem',
              maxWidth: '22rem',
              lineHeight: 1.45,
            }}
          >
            {quickMathPraise.subtitle}
          </p>
          {pointsBlock}
          <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.55)', margin: '1rem 0 0' }}>
            {!roundEnded
              ? t('bonus.player.waitForTimer')
              : hostIsPlaying
                ? t('bonus.player.roundOverWaiting')
                : t('bonus.player.roundOverWatchHost')}
          </p>
        </>
      ) : (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>{showEarlyResult ? '✅' : '⏰'}</div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#F8FAFC', marginBottom: '0.5rem' }}>
            {showEarlyResult ? t('bonus.player.answerSubmittedExclaim') : t('bonus.player.timesUp')}
          </h2>
          {pointsBlock}
        </>
      )}
    </div>
  );

  // ── "Bonus Level Finished" + spinner below overlay (only when round fully ended) ──
  const outroFooter = roundEnded && (
    <div style={{ textAlign: 'center', padding: '1.5rem 1rem 0', animation: 'fadeIn 0.4s ease-out' }}>
      <p style={{ fontSize: '1.4rem', fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '0.3rem' }}>
        {t('bonus.player.bonusLevelFinished')}
      </p>
      <WaitingSpinner isLoading size="small" />
    </div>
  );

  const header = (
    <Header
      leftButton={{
        icon: isHost ? HeaderIcons.back : HeaderIcons.home,
        onClick: (isHost ? onBackToGameSelection : onGoHome) ?? (() => {}),
        tooltip: isHost ? t('gameplay.backToGameSelection') : t('gameplay.leaveGame'),
      }}
      rightButton={{
        icon: HeaderIcons.refresh,
        onClick: () => window.location.reload(),
        tooltip: t('common.tooltips.refreshPage'),
      }}
      centerContent={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '1rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{t('gameplay.roomLabel')}</span>
          <span style={{
            fontSize: '1.2rem',
            fontWeight: 'bold',
            color: 'var(--color-primary)',
            padding: '0.3rem 0.6rem',
            backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
            borderRadius: '6px',
            border: '2px solid var(--color-primary)',
          }}>
            {roomState.roomName}
          </span>
        </div>
      }
    />
  );

  if (!payload) {
    return (
      <div className="app">
        {header}
        <div style={{ padding: '2rem' }}>{t('bonus.host.loadingBonus')}</div>
      </div>
    );
  }

  // When timer bar is hidden (roundEnded or showEarlyResult), container needs its own top padding
  const contentPadTop = (roundEnded || showEarlyResult) ? '4.5rem' : '0.5rem';
  const showOverlayInsteadOfGame = roundEnded || showEarlyResult || showQuickMathChainOverlay;

  // Host not playing: only the game interaction (no intro, no result on player)
  if (!hostIsPlaying) {
    return (
      <div className="app app--bonus-player">
        {header}
        <div className="bonus-player__scroll">
          {!showOverlayInsteadOfGame && timerBar}
          <div
          className="container bonus-player-content"
          style={{
            paddingTop: contentPadTop,
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
            {!showOverlayInsteadOfGame ? gameContent : overlay}
          </div>
          {outroFooter}
        </div>
      </div>
    );
  }

  // Host is playing: show game only (no title/description on player)
  return (
    <div className="app app--bonus-player">
      {header}
      <div className="bonus-player__scroll">
        {!showOverlayInsteadOfGame && timerBar}
        <div
          className="container bonus-player-content"
          style={{
            paddingTop: contentPadTop,
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            maxWidth: 600,
            margin: '0 auto',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!showOverlayInsteadOfGame ? (
            <>
              {hostPlayingIntroBannerEl}
              {gameContent}
            </>
          ) : (
            overlay
          )}
        </div>
        {outroFooter}
      </div>
    </div>
  );
};
